import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import {
  colors,
  fonts,
  radii,
  breakpoints,
  containerStyle,
  SERVICE_CATEGORIES,
  getServiceCategory,
  serviceCategoryColors,
} from './designSystem';
import { icone } from '../../annuaire/tunnel/ui';
import PanneauBas from './PanneauBas';

// ---------------------------------------------------------------------------
// Build filter groups from the SERVICE_CATEGORIES mapping
// ---------------------------------------------------------------------------

const FILTER_GROUPS = {
  vente: {
    label: 'Vente',
    services: Object.keys(SERVICE_CATEGORIES).filter(
      (s) => SERVICE_CATEGORIES[s] === 'vente',
    ),
  },
  atelier: {
    label: 'Atelier',
    services: Object.keys(SERVICE_CATEGORIES).filter(
      (s) => SERVICE_CATEGORIES[s] === 'atelier',
    ),
  },
  extra: {
    label: 'Services +',
    services: Object.keys(SERVICE_CATEGORIES).filter(
      (s) => SERVICE_CATEGORIES[s] === 'extra',
    ),
  },
};

const CATEGORY_BG = {
  vente: colors.bleuPastel,
  atelier: colors.violetPastel,
  extra: colors.orangePastel,
};

const SORT_OPTIONS = [
  { value: 'note', label: 'Trier : Mieux notés' },
  { value: 'avis', label: "Trier : Plus d'avis" },
  { value: 'nom', label: 'Trier : A-Z' },
];

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

/* L'en-tête du site ne colle qu'à partir de 992 px (78 px visibles) : en
   dessous, la barre colle tout en haut. */
const StickyWrapper = styled.div`
  position: sticky;
  top: 0;

  @media (min-width: 992px) {
    top: 78px;
  }

  z-index: 100;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.borderLight};
  padding: 14px 0;
`;

const Container = styled.div`
  ${containerStyle}
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;

  /* Mobile (« piste B », validée le 22/09) : « Réservable en ligne »,
     « Filtres », puis le tri en icône, sur une ligne. Avec le second filtre
     (« Applique le Bonus Répar ») la ligne dépasserait l'écran et ferait
     défiler toute la page de côté : elle passe alors à la ligne. */
  @media (max-width: 767px) {
    gap: 8px;
  }
`;

/** Ce qui ne sert que sur ordinateur (menus déroulants, tri, pastilles). */
const Ordi = styled.div`
  display: contents;

  @media (max-width: 767px) {
    display: none;
  }
`;

const BoutonMobile = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  height: 36px;
  padding: 0 14px;
  border-radius: 50px;
  border: 1.5px solid ${colors.borderLight};
  background: ${colors.white};
  color: ${colors.bleuFonce};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  white-space: nowrap;

  &[data-icone] {
    width: 36px;
    padding: 0;
    margin-left: auto;
  }

  @media (max-width: 767px) {
    display: inline-flex;
  }
`;

const Pastille = styled.span`
  min-width: 18px;
  padding: 1px 6px;
  border-radius: 10px;
  background: ${colors.bleu};
  color: ${colors.white};
  font-size: 0.7rem;
  line-height: 16px;
`;

const Groupe = styled.fieldset`
  margin: 16px 0 0;
  padding: 0;
  border: none;

  legend {
    padding: 0;
    margin-bottom: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.textSecondary};
  }
`;

const Choix = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 12px 0;
  border-bottom: 1px solid #eef1f4;
  font-size: 0.95rem;
  font-weight: 400;
  cursor: pointer;

  input {
    width: 20px;
    height: 20px;
    margin: 0;
    accent-color: ${colors.bleuFonce};
  }
`;

const Effacer = styled.button`
  border: none;
  background: none;
  padding: 4px 0;
  color: ${colors.bleu};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
`;

const Voir = styled.button`
  width: 100%;
  min-height: 48px;
  border: none;
  border-radius: 14px;
  background: ${colors.vert};
  color: ${colors.bleuFonce};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
`;

const IconeTri = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true">
    <path d="M7 4v16M7 20l-3-3M7 20l3-3M17 20V4M17 4l-3 3M17 4l3 3" />
  </svg>
);

const IconeFiltres = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true">
    <path d="M4 6h16M7 12h10M10 18h4" />
  </svg>
);

const voirAteliers = (n) => {
  if (n === 0) return 'Aucun atelier';
  return n === 1 ? 'Voir 1 atelier' : `Voir ${n} ateliers`;
};

/** « Réservable en ligne (N) », premier des filtres (lot 3, validé le 22/09). */
const EnLigneChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 7px 14px;
  border-radius: 50px;
  border: 1.5px solid ${colors.bleuFonce};
  background: ${(p) => (p.$actif ? colors.bleuFonce : colors.white)};
  color: ${(p) => (p.$actif ? colors.white : colors.bleuFonce)};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  white-space: nowrap;

  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

/** « Applique le » ne tient pas sur la ligne d'un téléphone : l'étiquette y
    est « Bonus Répar (N) », et le nom accessible garde la phrase entière. */
const PrefixeOrdi = styled.span`
  @media (max-width: 767px) {
    display: none;
  }
`;

const FilterLabel = styled.span`
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 0.85rem;
  color: ${colors.textDark};
  flex-shrink: 0;
  margin-right: 4px;

  @media (max-width: 767px) {
    font-size: 0.8rem;
    margin-right: 0;
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1.5px solid ${(p) => (p.$active ? colors.bleuFonce : 'transparent')};
  border-radius: ${radii.pill};
  background-color: ${(p) => p.$bg || colors.white};
  color: ${colors.bleuFonce};
  font-family: ${fonts.body};
  font-size: 0.8rem;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    border-color: ${colors.bleuFonce};
  }

  @media (max-width: 767px) {
    font-size: 0.78rem;
    padding: 7px 12px;
  }
`;

const ChevronSvg = styled.svg`
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  transition: transform 0.2s ease;
  transform: ${(p) => (p.$open ? 'rotate(180deg)' : 'none')};
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: ${colors.bleuFonce};
  color: ${colors.white};
  font-size: 0.65rem;
  font-weight: 700;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 240px;
  background-color: ${colors.white};
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(49, 73, 89, 0.15);
  padding: 12px 0;
  z-index: 200;
`;

const MenuOption = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: 0.85rem;
  color: ${colors.textDark};
  transition: background 0.1s ease;

  &:hover {
    background-color: ${colors.grisClair};
  }
`;

const Checkbox = styled.input`
  accent-color: ${colors.bleuFonce};
  width: 18px;
  height: 18px;
`;

const SortSelect = styled.select`
  padding: 8px 32px 8px 12px;
  border: 1.5px solid ${colors.borderLight};
  border-radius: ${radii.pill};
  background-color: ${colors.white};
  color: ${colors.textDark};
  font-family: ${fonts.body};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  appearance: none;
  margin-left: auto;
  flex-shrink: 0;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6' fill='%23667085'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;

  &:focus {
    outline: none;
    border-color: ${colors.bleu};
  }

  @media (max-width: 767px) {
    font-size: 0.78rem;
    padding: 7px 12px;
    margin-left: 0;
    width: 100%;
    margin-top: 4px;
  }
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: ${(p) => (p.$hasChips ? '12px' : '0')};

  /* Sur mobile, la pastille du bouton « Filtres » compte les choix. */
  @media (max-width: 767px) {
    display: none;
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: ${radii.pill};
  background-color: ${(p) => p.$bg || colors.bleuPastel};
  color: ${colors.bleuFonce};
  font-family: ${fonts.body};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const ChipRemove = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1;
`;

const ClearAll = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: 0.78rem;
  font-weight: 500;
  color: ${colors.textSecondary};
  padding: 8px 14px;
  border-radius: ${radii.pill};
  flex-shrink: 0;

  &:hover {
    color: ${colors.bleuFonce};
  }
`;

// ---------------------------------------------------------------------------
// Dropdown sub-component
// ---------------------------------------------------------------------------

function FilterDropdown({ label, category, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const hasActive = selected.length > 0;
  const bg = CATEGORY_BG[category] || colors.white;

  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter((v) => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  return (
    <DropdownWrapper ref={ref}>
      <DropdownButton
        $active={hasActive}
        $bg={bg}
        onClick={() => setOpen(!open)}
        type="button">
        {label}
        {hasActive && <CountBadge>{selected.length}</CountBadge>}
        <ChevronSvg
          $open={open}
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </ChevronSvg>
      </DropdownButton>
      {open && (
        <DropdownMenu>
          {options.map((opt) => (
            <MenuOption key={opt}>
              <Checkbox
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
              />
              {opt}
            </MenuOption>
          ))}
        </DropdownMenu>
      )}
    </DropdownWrapper>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function FiltersBar({
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  resultCount,
  enLigne = null,
  bonus = null,
}) {
  const [panneau, setPanneau] = useState(null); // null | 'filtres' | 'tri'
  const { vente = [], atelier = [], extra = [] } = filters || {};
  const allSelected = [...vente, ...atelier, ...extra];
  const hasChips = allSelected.length > 0;

  const updateCategory = (category, newVal) => {
    onFiltersChange({ ...filters, [category]: newVal });
  };

  const removeChip = (service) => {
    const cat = getServiceCategory(service);
    const key =
      cat === 'vente' ? 'vente' : cat === 'atelier' ? 'atelier' : 'extra';
    updateCategory(
      key,
      (filters[key] || []).filter((s) => s !== service),
    );
  };

  // Filtrée en direct derrière le panneau, la liste a pu raccourcir sous la
  // position de défilement : on remonte au début des résultats.
  const barre = useRef(null);
  const voirResultats = () => {
    setPanneau(null);
    const liste = barre.current && barre.current.nextElementSibling;
    if (!liste) return;
    window.requestAnimationFrame(() => {
      const haut =
        liste.getBoundingClientRect().top +
        window.scrollY -
        barre.current.offsetHeight;
      if (window.scrollY > haut) window.scrollTo(0, haut);
    });
  };

  const clearAll = () => {
    onFiltersChange({ vente: [], atelier: [], extra: [] });
  };

  return (
    <StickyWrapper ref={barre}>
      <Container>
        <FiltersRow>
          <Ordi>
            <FilterLabel>Filtrer :</FilterLabel>
          </Ordi>

          {enLigne && (enLigne.count > 0 || enLigne.actif) && (
            <EnLigneChip
              type="button"
              aria-pressed={enLigne.actif}
              $actif={enLigne.actif}
              onClick={enLigne.onToggle}>
              {!enLigne.actif && <img src={icone('ENERGIE')} alt="" />}
              Réservable en ligne ({enLigne.count})
              {enLigne.actif && <span aria-hidden="true"> &times;</span>}
            </EnLigneChip>
          )}

          {bonus && (bonus.count > 0 || bonus.actif) && (
            <EnLigneChip
              type="button"
              aria-pressed={bonus.actif}
              aria-label={`Applique le Bonus Répar (${bonus.count})`}
              $actif={bonus.actif}
              onClick={bonus.onToggle}>
              {!bonus.actif && <img src={icone('TARIF_EUROS')} alt="" />}
              <span>
                <PrefixeOrdi>Applique le </PrefixeOrdi>Bonus Répar (
                {bonus.count})
              </span>
              {bonus.actif && <span aria-hidden="true"> &times;</span>}
            </EnLigneChip>
          )}

          <BoutonMobile
            type="button"
            aria-haspopup="dialog"
            onClick={() => setPanneau('filtres')}>
            <IconeFiltres />
            Filtres
            {hasChips && <Pastille>{allSelected.length}</Pastille>}
          </BoutonMobile>
          <BoutonMobile
            type="button"
            data-icone
            aria-haspopup="dialog"
            aria-label={
              (SORT_OPTIONS.find((o) => o.value === sort) || SORT_OPTIONS[0])
                .label
            }
            onClick={() => setPanneau('tri')}>
            <IconeTri />
          </BoutonMobile>

          <Ordi>
            <FilterDropdown
              label={FILTER_GROUPS.vente.label}
              category="vente"
              options={FILTER_GROUPS.vente.services}
              selected={vente}
              onChange={(v) => updateCategory('vente', v)}
            />
            <FilterDropdown
              label={FILTER_GROUPS.atelier.label}
              category="atelier"
              options={FILTER_GROUPS.atelier.services}
              selected={atelier}
              onChange={(v) => updateCategory('atelier', v)}
            />
            <FilterDropdown
              label={FILTER_GROUPS.extra.label}
              category="extra"
              options={FILTER_GROUPS.extra.services}
              selected={extra}
              onChange={(v) => updateCategory('extra', v)}
            />

            {hasChips && (
              <ClearAll onClick={clearAll} type="button">
                &times; Effacer
              </ClearAll>
            )}

            <SortSelect
              value={sort}
              onChange={(e) => onSortChange(e.target.value)}>
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </SortSelect>
          </Ordi>
        </FiltersRow>

        {hasChips && (
          <ChipsRow $hasChips>
            {allSelected.map((service) => {
              const cat = getServiceCategory(service);
              const chipBg = CATEGORY_BG[cat] || colors.bleuPastel;
              return (
                <Chip
                  key={service}
                  $bg={chipBg}
                  onClick={() => removeChip(service)}>
                  {service}
                  <ChipRemove>&times;</ChipRemove>
                </Chip>
              );
            })}
          </ChipsRow>
        )}
      </Container>

      {panneau === 'filtres' && (
        <PanneauBas
          titre="Filtres"
          onFermer={() => setPanneau(null)}
          action={
            hasChips && (
              <Effacer type="button" onClick={clearAll}>
                Tout effacer
              </Effacer>
            )
          }
          pied={
            <Voir type="button" onClick={voirResultats}>
              {voirAteliers(resultCount)}
            </Voir>
          }>
          {Object.keys(FILTER_GROUPS).map((cle) => (
            <Groupe key={cle}>
              <legend>{FILTER_GROUPS[cle].label}</legend>
              {FILTER_GROUPS[cle].services.map((service) => {
                const choisis = { vente, atelier, extra }[cle];
                const coche = choisis.includes(service);
                return (
                  <Choix key={service}>
                    <input
                      type="checkbox"
                      checked={coche}
                      onChange={() =>
                        updateCategory(
                          cle,
                          coche
                            ? choisis.filter((v) => v !== service)
                            : [...choisis, service],
                        )
                      }
                    />
                    {service}
                  </Choix>
                );
              })}
            </Groupe>
          ))}
        </PanneauBas>
      )}

      {panneau === 'tri' && (
        <PanneauBas titre="Trier" onFermer={() => setPanneau(null)}>
          {SORT_OPTIONS.map((opt) => (
            <Choix key={opt.value}>
              <input
                type="radio"
                name="tri-mobile"
                checked={sort === opt.value}
                // Le tri s'applique à chaque changement ; le panneau se
                // referme au toucher (même sur le tri déjà choisi), pas aux
                // flèches du clavier, qui changent aussi la case cochée.
                onChange={() => onSortChange(opt.value)}
                onClick={(e) => {
                  if (e.detail > 0) setPanneau(null);
                }}
              />
              {opt.label.replace('Trier : ', '')}
            </Choix>
          ))}
        </PanneauBas>
      )}
    </StickyWrapper>
  );
}
